import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { corporateTheme } from '../themes/corporate';

const AnalysisContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 3rem;
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: ${corporateTheme.fontFamily};
`;

const ContentSection = styled(motion.div)`
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h3`
  font-size: 2rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  color: ${corporateTheme.primary};
  margin-bottom: 2rem;
  text-align: center;
  font-family: ${corporateTheme.fontFamily};
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 4rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ChartContainer = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(10px);
  border-radius: ${corporateTheme.borderRadius.xl};
  padding: 2rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  height: 400px;
`;

const ChartTitle = styled.h4`
  font-size: 1.3rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  color: ${corporateTheme.textPrimary};
  margin-bottom: 1.5rem;
  text-align: center;
  font-family: ${corporateTheme.fontFamily};
`;

const MarketGrid = styled.div`
  display: grid;
  /* 6 cards: 3x2 layout for better balance */
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 1200px) {
    /* Switch to 2 columns for medium screens */
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    /* Switch to auto-fit for small screens */
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
`;

const MarketCard = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(5px);
  border-radius: ${corporateTheme.borderRadius.lg};
  padding: 2rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.color || corporateTheme.primaryGradient};
  }
`;

const CountryCode = styled.div`
  font-size: 1.2rem;
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.background};
  background: ${corporateTheme.primaryGradient};
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: 0 auto 1rem;
  font-family: ${corporateTheme.fontFamily};
  letter-spacing: 0.5px;
`;

const CountryName = styled.h4`
  font-size: 1.4rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const MarketStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.primary};
  font-family: ${corporateTheme.fontFamily};
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: ${corporateTheme.textSecondary};
  margin-top: 0.3rem;
  font-family: ${corporateTheme.fontFamily};
`;

const MarketAnalysis = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  // Market size data for Eastern Asian countries with darker colors
  const marketSizeData = [
    { name: 'China', value: 1980, color: corporateTheme.chartColors.primary },
    { name: 'Japan', value: 156, color: corporateTheme.chartColors.secondary },
    { name: 'South Korea', value: 87, color: corporateTheme.chartColors.tertiary },
    { name: 'Singapore', value: 12, color: corporateTheme.chartColors.quaternary },
    { name: 'Malaysia', value: 24, color: corporateTheme.chartColors.quinary },
    { name: 'Thailand', value: 18, color: corporateTheme.chartColors.senary },
    { name: 'Vietnam', value: 13, color: corporateTheme.success },
    { name: 'Indonesia', value: 44, color: corporateTheme.warning }
  ];

  // Internet penetration data
  const internetData = [
    { country: 'China', penetration: 73 },
    { country: 'Japan', penetration: 93 },
    { country: 'S.Korea', penetration: 96 },
    { country: 'Singapore', penetration: 89 },
    { country: 'Malaysia', penetration: 84 },
    { country: 'Thailand', penetration: 77 },
    { country: 'Vietnam', penetration: 68 },
    { country: 'Indonesia', penetration: 64 }
  ];

  // Growth projection data
  const growthData = [
    { year: '2024', revenue: 100 },
    { year: '2025', revenue: 280 },
    { year: '2026', revenue: 520 },
    { year: '2027', revenue: 850 },
    { year: '2028', revenue: 1200 }
  ];

  const marketDetails = [
    {
      code: 'CN',
      name: 'China',
      population: '1.4B',
      ecommerce: '$1.98T',
      growth: '+8.5%',
      mobile: '97%',
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.primary} 0%, ${corporateTheme.primary} 100%)`
    },
    {
      code: 'JP',
      name: 'Japan',
      population: '125M',
      ecommerce: '$156B',
      growth: '+4.2%',
      mobile: '85%',
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.secondary} 0%, ${corporateTheme.success} 100%)`
    },
    {
      code: 'KR',
      name: 'South Korea',
      population: '52M',
      ecommerce: '$87B',
      growth: '+12.3%',
      mobile: '92%',
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.tertiary} 0%, ${corporateTheme.error} 100%)`
    },
    {
      code: 'SG',
      name: 'Singapore',
      population: '6M',
      ecommerce: '$12B',
      growth: '+15.7%',
      mobile: '94%',
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.quaternary} 0%, ${corporateTheme.warning} 100%)`
    },
    {
      code: 'MY',
      name: 'Malaysia',
      population: '33M',
      ecommerce: '$24B',
      growth: '+18.2%',
      mobile: '88%',
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.quinary} 0%, ${corporateTheme.accent} 100%)`
    },
    {
      code: 'TH',
      name: 'Thailand',
      population: '70M',
      ecommerce: '$18B',
      growth: '+22.1%',
      mobile: '91%',
      color: `linear-gradient(90deg, ${corporateTheme.chartColors.senary} 0%, ${corporateTheme.accentLight} 100%)`
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: corporateTheme.background,
          padding: '10px',
          border: `1px solid ${corporateTheme.cardBorder}`,
          borderRadius: '8px',
          color: corporateTheme.textPrimary,
          boxShadow: corporateTheme.cardShadow,
          fontFamily: corporateTheme.fontFamily
        }}>
          <p>{`${label}: ${payload[0].value}${payload[0].name === 'penetration' ? '%' : payload[0].name === 'value' ? 'B USD' : ''}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <AnalysisContainer ref={ref}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <Title variants={itemVariants}>
          Market Analysis
        </Title>

        <ContentSection variants={itemVariants}>
          <SectionTitle>Eastern Asian E-commerce Market Size (2024)</SectionTitle>
          <ChartGrid>
            <ChartContainer variants={itemVariants}>
              <ChartTitle>Market Size by Country (Billions USD)</ChartTitle>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={marketSizeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={corporateTheme.cardBorder} />
                  <XAxis 
                    dataKey="name" 
                    stroke={corporateTheme.textSecondary} 
                    fontSize={12}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis stroke={corporateTheme.textSecondary} fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill={corporateTheme.chartColors.primary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>

            <ChartContainer variants={itemVariants}>
              <ChartTitle>Internet Penetration Rates (%)</ChartTitle>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={internetData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={corporateTheme.cardBorder} />
                  <XAxis 
                    dataKey="country" 
                    stroke={corporateTheme.textSecondary} 
                    fontSize={12}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis stroke={corporateTheme.textSecondary} fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="penetration" fill={corporateTheme.chartColors.secondary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </ChartGrid>
        </ContentSection>

        <ContentSection variants={itemVariants}>
          <SectionTitle>Revenue Projections (Millions EUR)</SectionTitle>
          <ChartContainer variants={itemVariants} style={{ margin: '0 auto', maxWidth: '800px' }}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" stroke={corporateTheme.cardBorder} />
                <XAxis dataKey="year" stroke={corporateTheme.textSecondary} fontSize={12} />
                <YAxis stroke={corporateTheme.textSecondary} fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke={corporateTheme.chartColors.primary} 
                  strokeWidth={3}
                  dot={{ fill: corporateTheme.chartColors.primary, strokeWidth: 2, r: 6 }}
                  activeDot={{ r: 8, fill: corporateTheme.chartColors.primary }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </ContentSection>

        <ContentSection variants={itemVariants}>
          <SectionTitle>Target Market Overview</SectionTitle>
          <MarketGrid>
            {marketDetails.map((market, index) => (
              <MarketCard
                key={market.name}
                variants={itemVariants}
                color={market.color}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <CountryCode>{market.code}</CountryCode>
                <CountryName>{market.name}</CountryName>
                <MarketStats>
                  <StatItem>
                    <StatValue>{market.population}</StatValue>
                    <StatLabel>Population</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatValue>{market.ecommerce}</StatValue>
                    <StatLabel>E-commerce</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatValue>{market.growth}</StatValue>
                    <StatLabel>Growth Rate</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatValue>{market.mobile}</StatValue>
                    <StatLabel>Mobile Usage</StatLabel>
                  </StatItem>
                </MarketStats>
              </MarketCard>
            ))}
          </MarketGrid>
        </ContentSection>
      </motion.div>
    </AnalysisContainer>
  );
};

export default MarketAnalysis; 